import { EmployeeModel } from '@app/reducers/orm/employee/employee.model';
import { ShiftModel } from '@app/reducers/orm/shift/shift.model';
import { TeamModel, TeamType } from '@app/reducers/orm/team/team.model';

import type { ShiftAssignment, TeamShiftOption } from './dev-tools-hydration.models';
import { getRandomElement } from './dev-tools-mock-data';

function groupShiftsByDepartment(shiftTemplates: ShiftModel[]): Map<string, ShiftModel[]> {
  const shiftsByDepartment = new Map<string, ShiftModel[]>();

  shiftTemplates.forEach((shiftTemplate) => {
    const departmentShifts = shiftsByDepartment.get(shiftTemplate.department_id) ?? [];
    shiftsByDepartment.set(shiftTemplate.department_id, [...departmentShifts, shiftTemplate]);
  });

  return shiftsByDepartment;
}

/**
 * Resolves every default team that has a shift template in its department —
 * scheduled shift batches pick a random team and shift template from these.
 */
export function resolveTeamShiftOptions(teams: TeamModel[], shiftTemplates: ShiftModel[]): TeamShiftOption[] {
  const shiftsByDepartment = groupShiftsByDepartment(shiftTemplates);

  return teams
    .filter((team) => team.type === TeamType.DEFAULT)
    .flatMap((team) => {
      const departmentShifts = shiftsByDepartment.get(team.department_id);

      if (!departmentShifts?.length) {
        return [];
      }

      return [
        {
          teamId: team.id,
          departmentId: team.department_id,
          shiftIds: departmentShifts.map((shiftTemplate) => shiftTemplate.id),
        },
      ];
    });
}

/**
 * Resolves the user → team → department → shift template chain for every employee.
 * Employees without a default team in a department that has a shift template are
 * left out, as timesheets need all four ids.
 */
export function resolveAssignments(
  employees: EmployeeModel[],
  teams: TeamModel[],
  shiftTemplates: ShiftModel[],
): ShiftAssignment[] {
  const teamsById = new Map(teams.map((team) => [team.id, team]));
  const shiftsByDepartment = groupShiftsByDepartment(shiftTemplates);

  const assignments: ShiftAssignment[] = [];

  employees.forEach((employee) => {
    if (!employee.id) {
      return;
    }

    for (const teamId of employee.Team ?? []) {
      const team = teamsById.get(teamId);

      if (!team || team.type !== TeamType.DEFAULT) {
        continue;
      }

      const departmentShifts = shiftsByDepartment.get(team.department_id);

      if (departmentShifts?.length) {
        assignments.push({
          userId: employee.id,
          teamId,
          departmentId: team.department_id,
          shiftId: getRandomElement(departmentShifts).id,
        });
        break;
      }
    }
  });

  return assignments;
}
